import { FeatureGateAudience } from '../../__generated__/globalTypes';

import { ruleToFormState } from './ruleSerialization';

import type { FeatureGateRuleNode } from './ruleTree';

/**
 * Computes the row-level diff shown in the edit-save confirmation
 * dialog. The dialog needs the smallest possible signal — just the
 * fields that actually changed — so the operator can scan it in one
 * read before flipping the trigger.
 *
 * Pure value-only logic so the comparison rules stay testable in
 * isolation. The dialog is responsible for layout / styling; this
 * helper only emits the row data.
 *
 * Lookups by rule shape go through `ruleToFormState`, which is
 * order-tolerant on the OR composite — re-saving an unchanged composite
 * after the form serialized it in a different child order will not show
 * a phantom rule diff.
 */

export interface IGateSnapshot {
  isEnabled: boolean;
  ruleTree: FeatureGateRuleNode;
  audience: FeatureGateAudience;
  defaultWhenError: boolean;
  description: string;
}

export interface IDiffRow {
  /** Stable field key — used as a React key. */
  field: keyof IGateSnapshot;
  /** Human-readable field label rendered as the row's left column. */
  label: string;
  /** String form of the previous value. Empty string is rendered as `—`. */
  before: string;
  /** String form of the new value. */
  after: string;
}

/**
 * Returns the changed-field rows between two gate snapshots, in field
 * order. An empty list means the form is byte-for-byte unchanged — the
 * caller should disable the save button in that case rather than
 * opening an empty dialog.
 *
 * Comparisons are value-equal (no object identity assumptions) so a
 * gate that round-trips through the form (rule tree → form state →
 * rule tree) does not emit a phantom diff just because the JS object
 * reference changed.
 */
export function diffGate(prev: IGateSnapshot, next: IGateSnapshot): IDiffRow[] {
  const rows: IDiffRow[] = [];

  if (prev.isEnabled !== next.isEnabled) {
    rows.push({
      field: 'isEnabled',
      label: 'Enabled',
      before: prev.isEnabled ? 'On' : 'Off',
      after: next.isEnabled ? 'On' : 'Off',
    });
  }

  const prevForm = ruleToFormState(prev.ruleTree);
  const nextForm = ruleToFormState(next.ruleTree);
  if (prevForm.adminPct !== nextForm.adminPct) {
    rows.push({
      field: 'ruleTree',
      label: 'Admins rollout %',
      before: `${prevForm.adminPct}`,
      after: `${nextForm.adminPct}`,
    });
  }
  if (prevForm.everyonePct !== nextForm.everyonePct) {
    rows.push({
      field: 'ruleTree',
      label: 'Everyone rollout %',
      before: `${prevForm.everyonePct}`,
      after: `${nextForm.everyonePct}`,
    });
  }

  if (prev.audience !== next.audience) {
    rows.push({
      field: 'audience',
      label: 'Audience',
      before: AUDIENCE_LABEL[prev.audience],
      after: AUDIENCE_LABEL[next.audience],
    });
  }

  if (prev.defaultWhenError !== next.defaultWhenError) {
    rows.push({
      field: 'defaultWhenError',
      label: 'Default on evaluation failure',
      before: prev.defaultWhenError ? 'On' : 'Off',
      after: next.defaultWhenError ? 'On' : 'Off',
    });
  }

  if (prev.description !== next.description) {
    rows.push({
      field: 'description',
      label: 'Description',
      before: prev.description,
      after: next.description,
    });
  }

  return rows;
}

const AUDIENCE_LABEL: Record<FeatureGateAudience, string> = {
  [FeatureGateAudience.ServerOnly]: 'Server only',
  [FeatureGateAudience.WebClient]: 'User app',
  [FeatureGateAudience.MopsWebClient]: 'Admin app',
  [FeatureGateAudience.AllClients]: 'User + admin apps',
};
